import { lazy, useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useColorScheme } from "@mui/material/styles";

import { PortalLayout } from "./components/PortalLayout";
import { AdminRequestWorkflow } from "./components/AdminRequestWorkflow";
import { PageLoadingState } from "./components/LoadingState";
import { useSession } from "./hooks/useSession";
import { ALTO_PANTALLA, marcaFija } from "./theme/theme";

import LoginPage from "./pages/LoginPage";
import TwoFactorPage from "./pages/TwoFactorPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PrivacyPage from "./pages/PrivacyPage";
import NotFoundPage from "./pages/NotFoundPage";

// Las pantallas del portal solo las necesita quien ha iniciado sesión:
// se cargan bajo demanda para que el acceso siga siendo ligero.
const CHUNK_RELOAD_KEY = "atlas:chunk-reload";

/**
 * Recupera una pestaña que conserva el HTML de una versión anterior.
 *
 * Después de publicar, esa pestaña puede pedir por primera vez un fragmento
 * con un hash ya retirado. React no puede resolverlo reintentando el mismo
 * archivo: necesita cargar el índice nuevo. Se recarga una sola vez y se usa
 * una marca temporal para impedir un bucle si el fallo no era de versión.
 */
function lazyPage(loader) {
  return lazy(async () => {
    try {
      const module = await loader();
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return module;
    } catch (error) {
      const message = String(error?.message ?? error);
      const isChunkFailure =
        /dynamically imported module|module script|loading chunk|chunkloaderror/i.test(
          message,
        );
      const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);

      if (isChunkFailure && Date.now() - lastReload > 15_000) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    }
  });
}

const DashboardPage = lazyPage(() => import("./pages/DashboardPage"));
const NewRequestPage = lazyPage(() => import("./pages/NewRequestPage"));
const ProcessingPage = lazyPage(() => import("./pages/ProcessingPage"));
const InstallationVerificationPage = lazyPage(() => import("./pages/InstallationVerificationPage"));
const ConfirmPage = lazyPage(() => import("./pages/ConfirmPage"));
const ValuationPage = lazyPage(() => import("./pages/ValuationPage"));
const RecommendationsPage = lazyPage(() => import("./pages/RecommendationsPage"));
const RequestSentPage = lazyPage(() => import("./pages/RequestSentPage"));
const RequestsPage = lazyPage(() => import("./pages/RequestsPage"));
const RequestDetailPage = lazyPage(() => import("./pages/RequestDetailPage"));
const BudgetsPage = lazyPage(() => import("./pages/BudgetsPage"));
const BudgetDetailPage = lazyPage(() => import("./pages/BudgetDetailPage"));
const NotificationsPage = lazyPage(() => import("./pages/NotificationsPage"));
const ProfilePage = lazyPage(() => import("./pages/ProfilePage"));
const PreferencesPage = lazyPage(() => import("./pages/PreferencesPage"));
const HelpPage = lazyPage(() => import("./pages/HelpPage"));
const GuidePage = lazyPage(() => import("./pages/GuidePage"));
const SupportPage = lazyPage(() => import("./pages/SupportPage"));
const AssistantPage = lazyPage(() => import("./pages/AssistantPage"));
const AdminPage = lazyPage(() => import("./pages/AdminPage"));

/**
 * Pinta del color del portal lo que el navegador dibuja alrededor.
 *
 * En un móvil la barra de estado y la de direcciones se tiñen con este valor.
 * Estaba clavado en un azul que ya no aparece en ninguna pantalla, y con el
 * tema oscuro el problema se agrava: un marco claro rodeando una aplicación
 * oscura es exactamente el borde que delata que la app termina ahí.
 *
 * Se hace desde aquí y no con dos etiquetas y una consulta de medios en el
 * HTML, porque el tema no siempre lo decide el sistema: si se ha elegido
 * claro a mano teniendo el equipo en oscuro, la consulta de medios acertaría
 * el del sistema y fallaría el del portal.
 */
function useColorDeLaBarra() {
  const { mode, systemMode } = useColorScheme();
  const resuelto = mode === "system" ? systemMode : mode;

  useEffect(() => {
    if (!resuelto) return;
    const etiqueta = document.querySelector('meta[name="theme-color"]');
    if (!etiqueta) return;
    etiqueta.setAttribute(
      "content",
      resuelto === "dark" ? "#0e1319" : marcaFija.canvas,
    );
  }, [resuelto]);
}

function FullScreenLoader() {
  return <PageLoadingState height={ALTO_PANTALLA} />;
}

/** Exige sesión. Recuerda el destino para volver a él tras el acceso. */
function RequireAuth({ children }) {
  const { status } = useSession();
  const location = useLocation();

  if (status === "loading") return <FullScreenLoader />;
  if (status !== "authenticated") {
    return (
      <Navigate
        to="/"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  return children;
}

/**
 * Zona de personal de Adaltra. Es una comprobación de conveniencia para no
 * enseñar una pantalla vacía: quien manda es el rol que exige la API.
 */
function RequireStaff({ children }) {
  const { status, user } = useSession();
  if (status === "loading") return <FullScreenLoader />;
  if (!["commercial", "manager", "admin"].includes(user?.role))
    return <Navigate to="/inicio" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { status, user } = useSession();
  if (status === "loading") return <FullScreenLoader />;
  return user?.role === "admin" ? children : <Navigate to="/inicio" replace />;
}

/** El asistente puede estar apagado desde administración. */
function RequireAssistant({ children }) {
  const { status, features } = useSession();
  if (status === "loading") return <FullScreenLoader />;
  if (!features?.assistant) return <Navigate to="/inicio" replace />;
  return children;
}

/**
 * Una instancia de la página por solicitud. Al cambiar de referencia se monta
 * de nuevo, de modo que la respuesta tardía de una acción sobre otra solicitud
 * nunca sustituye los datos de la que se está mirando.
 */
function RequestDetailRoute(props) {
  const { ref } = useParams();
  return <RequestDetailPage key={ref} {...props} />;
}

/** Quien ya tiene sesión no debe quedarse en el acceso. */
function RedirectIfAuthenticated({ children }) {
  const { status } = useSession();
  if (status === "loading") return <FullScreenLoader />;
  if (status === "authenticated") return <Navigate to="/inicio" replace />;
  return children;
}

export default function App() {
  useColorDeLaBarra();

  return (
    <Routes>
      <Route
        path="/"
        element={
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route path="/recuperar-contrasena" element={<ForgotPasswordPage />} />
      <Route path="/verificacion-2fa" element={<RedirectIfAuthenticated><TwoFactorPage /></RedirectIfAuthenticated>} />
      <Route path="/restablecer-contrasena" element={<ResetPasswordPage />} />
      <Route path="/privacidad" element={<PrivacyPage />} />

      <Route
        element={
          <RequireAuth>
            <PortalLayout />
          </RequireAuth>
        }
      >
        <Route path="/inicio" element={<DashboardPage />} />
        <Route path="/lectura-pdf" element={<Navigate to="/nueva-solicitud" replace />} />
        <Route path="/administracion/solicitudes/:ref" element={<RequireAdmin><AdminRequestWorkflow /></RequireAdmin>}>
          <Route index element={<RequestDetailRoute />} />
          <Route path="editar" element={<NewRequestPage />} />
          <Route path="instalacion/:installationPhase" element={<NewRequestPage />} />
          <Route path="instalacion/verificacion" element={<InstallationVerificationPage />} />
          <Route path="procesando" element={<ProcessingPage />} />
          <Route path="revision" element={<RequestDetailRoute reviewMode />} />
          <Route path="recomendaciones" element={<RecommendationsPage />} />
          <Route path="confirmar" element={<ConfirmPage />} />
          <Route path="valoracion" element={<ValuationPage />} />
          <Route path="enviada" element={<RequestSentPage />} />
          <Route path="detalle" element={<RequestDetailRoute />} />
        </Route>
        <Route path="/administracion/presupuestos/:ref" element={<RequireAdmin><BudgetDetailPage readOnly /></RequireAdmin>} />
        <Route path="/nueva-solicitud" element={<NewRequestPage />} />
        <Route path="/nueva-solicitud/:ref" element={<NewRequestPage />} />
        <Route path="/nueva-solicitud/:ref/instalacion/:installationPhase" element={<NewRequestPage />} />
        <Route
          path="/nueva-solicitud/:ref/procesando"
          element={<ProcessingPage />}
        />
        <Route
          path="/nueva-solicitud/:ref/revision"
          element={<RequestDetailRoute reviewMode />}
        />
        <Route
          path="/nueva-solicitud/:ref/recomendaciones"
          element={<RecommendationsPage />}
        />
        <Route path="/nueva-solicitud/:ref/instalacion/verificacion" element={<InstallationVerificationPage />} />
        <Route path="/nueva-solicitud/:ref/valoracion" element={<ValuationPage />} />
        <Route
          path="/nueva-solicitud/:ref/confirmar"
          element={<ConfirmPage />}
        />
        <Route
          path="/nueva-solicitud/:ref/enviada"
          element={<RequestSentPage />}
        />
        <Route path="/solicitudes" element={<RequestsPage />} />
        <Route path="/solicitudes/:ref" element={<RequestDetailRoute />} />
        <Route path="/presupuestos" element={<BudgetsPage />} />
        <Route path="/presupuestos/:ref" element={<BudgetDetailPage />} />
        <Route path="/notificaciones" element={<NotificationsPage />} />
        <Route path="/perfil" element={<ProfilePage />} />
        <Route path="/preferencias" element={<PreferencesPage />} />
        <Route path="/ayuda" element={<HelpPage />} />
        <Route path="/ayuda/:slug" element={<GuidePage />} />
        <Route path="/soporte" element={<SupportPage />} />
        <Route
          path="/asistente"
          element={
            <RequireAssistant>
              <AssistantPage />
            </RequireAssistant>
          }
        />
        {/*
          Cada sección de Administración tiene su URL. `:seccion` opcional
          para que `/administracion` a secas siga funcionando —el menú
          lateral enlaza ahí— y caiga en la primera.
        */}
        <Route
          path="/administracion/:seccion?"
          element={
            <RequireStaff>
              <AdminPage />
            </RequireStaff>
          }
        />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
